import { ReactNode, useEffect, useId } from "react"
import { FieldPath, FieldValues, PathValue, UseFormReturn, get } from "react-hook-form"
import { lastDayOfMonth } from "date-fns"
import { Flex } from "../../../../../components/flex/Flex"
import { FormInput } from "./Input"
import { MonthSelect } from "./MonthSelect"
import { FormItem, FormLabel } from "./Form"
import { getFormError, getFormSuccess } from "../../../../../utils/form"

function isNumberPresent(value: number | undefined): value is number {
  return value !== undefined && !Number.isNaN(value)
}

type Props<T extends FieldValues> = {
  form: UseFormReturn<T>
  label: ReactNode
  dateFieldName: FieldPath<T>
  monthFieldName: FieldPath<T>
  yearFieldName: FieldPath<T>
}
export function DateInputBlock<T extends FieldValues>({
  form,
  dateFieldName,
  monthFieldName,
  yearFieldName,
  label,
}: Props<T>) {
  const id = useId()

  useEffect(() => {
    const subscription = form.watch((data, { name, type }) => {
      if (type !== "change") {
        return
      }

      if (name !== dateFieldName && name !== monthFieldName) {
        return
      }

      const date = get(data, dateFieldName)
      const month = get(data, monthFieldName)
      if (!isNumberPresent(date) || !isNumberPresent(month)) {
        return
      }

      const dateObject = new Date()
      dateObject.setMonth(month)
      const maxDate = lastDayOfMonth(dateObject).getDate()
      if (maxDate < date) {
        form.setValue(dateFieldName, maxDate as PathValue<T, FieldPath<T>>)
      }
    })

    return () => subscription.unsubscribe()
  }, [form, dateFieldName, monthFieldName])

  return (
    <>
      <FormLabel>{label}</FormLabel>
      <Flex css={{ gap: "5%" }} id={id}>
        <FormItem css={{ width: "22.5%" }}>
          <FormInput
            id={dateFieldName}
            type="number"
            placeholder="DD"
            {...form.register(dateFieldName, { valueAsNumber: true })}
            error={getFormError({ form, name: dateFieldName })}
            success={getFormSuccess({ form, name: dateFieldName })}
          />
        </FormItem>

        <FormItem css={{ width: "42.5%" }}>
          <MonthSelect
            {...form.register(monthFieldName, { valueAsNumber: true })}
            error={getFormError({ form, name: monthFieldName })}
            success={getFormSuccess({ form, name: monthFieldName })}
          />
        </FormItem>

        <FormItem css={{ width: "26.5%" }}>
          <FormInput
            id={yearFieldName}
            type="number"
            placeholder="RRRR"
            {...form.register(yearFieldName, { valueAsNumber: true })}
            error={getFormError({ form, name: yearFieldName })}
            success={getFormSuccess({ form, name: yearFieldName })}
          />
        </FormItem>
      </Flex>
    </>
  )
}
